import React, { useState, useEffect, useRef } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import IconButton from '@mui/material/IconButton';
import CircularProgress from '@mui/material/CircularProgress';
import CloseIcon from '@mui/icons-material/Close';
import PhoneAndroidIcon from '@mui/icons-material/PhoneAndroid';
import TextField from '@mui/material/TextField';
import FormControl from '@mui/material/FormControl';
import FormLabel from '@mui/material/FormLabel';
import RadioGroup from '@mui/material/RadioGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import Radio from '@mui/material/Radio';
import { Button } from '../Button/Button';
import {
  sendDownloadOtpApi,
  verifyDownloadOtpApi,
  verifyDownloadPasswordBypassApi,
} from '../../services/settingsService';
import { toastMessage } from '../../utils/toastMessage';

const RESEND_COOLDOWN = 60;
// Keep in sync with backend `new-app-server/rest-apis/modules/auths.js`
const PRIMARY_PHONE = '8700936627';
const FALLBACK_PHONE = '8287584290';
const TEST_PHONE = '8278313761';
const CONSULTANT_PAYOUT_PHONE = '8588877724';
const PHONE_PREFIX = '91';

function getMaskedPhone(phone) {
  const str = String(phone || '');
  if (str.length <= 4) return '*'.repeat(str.length || 10);
  return str.slice(0, 2) + '*'.repeat(str.length - 4) + str.slice(-2);
}

function normalizePhone10(phone) {
  const digits = String(phone || '').replace(/\D/g, '');
  if (digits.length < 10) return '';
  return digits.slice(-10);
}

function phoneForSmsTarget(smsTarget) {
  if (smsTarget === 'alternate') return FALLBACK_PHONE;
  if (smsTarget === 'test') return TEST_PHONE;
  if (smsTarget === 'consultantPayout') return CONSULTANT_PAYOUT_PHONE;
  return PRIMARY_PHONE;
}

function shouldShowAccountantPhone(activityModule = '') {
  const moduleName = String(activityModule || '').toLowerCase();
  return moduleName.includes('consultant payout') || moduleName.includes('bank/pan');
}

const DownloadOtpModal = ({
  open,
  onClose,
  onSuccess,
  onOtpVerified,
  actionPurpose = 'Sheet Download',
  otpPurpose = '',
  activityModule = '',
}) => {
  const [step, setStep] = useState('send');
  const [otp, setOtp] = useState('');
  const [adminPassword, setAdminPassword] = useState('');
  const [showPasswordBypass, setShowPasswordBypass] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [cooldown, setCooldown] = useState(0);
  const [targetPhone, setTargetPhone] = useState(PRIMARY_PHONE);
  const [smsTarget, setSmsTarget] = useState('primary');
  const timerRef = useRef(null);

  const adminEmail = localStorage.getItem('vedic_email') || '';
  const isSuperAdmin = String(localStorage.getItem('vedic_role') || '').trim().toLowerCase() === 'admin';
  const showAccountantPhone = shouldShowAccountantPhone(activityModule);
  const hasFallback = FALLBACK_PHONE !== PRIMARY_PHONE;

  useEffect(() => {
    if (open) {
      setStep('send');
      setOtp('');
      setAdminPassword('');
      setShowPasswordBypass(false);
      setError('');
      setLoading(false);
      setCooldown(0);
      setTargetPhone(PRIMARY_PHONE);
      setSmsTarget('primary');
      clearInterval(timerRef.current);
    }
    return () => clearInterval(timerRef.current);
  }, [open]);

  const startCooldown = () => {
    setCooldown(RESEND_COOLDOWN);
    timerRef.current = setInterval(() => {
      setCooldown((c) => {
        if (c <= 1) {
          clearInterval(timerRef.current);
          return 0;
        }
        return c - 1;
      });
    }, 1000);
  };

  const handleSendOtp = async (phone = targetPhone) => {
    const safePhone = normalizePhone10(
      typeof phone === 'string' || typeof phone === 'number' ? phone : targetPhone
    ) || targetPhone;

    setError('');
    setLoading(true);
    try {
      const { success, message } = await sendDownloadOtpApi(
        safePhone,
        PHONE_PREFIX,
        adminEmail,
        otpPurpose || actionPurpose,
        activityModule
      );
      if (success) {
        setTargetPhone(safePhone);
        setSmsTarget(
          safePhone === CONSULTANT_PAYOUT_PHONE ? 'consultantPayout' :
            safePhone === FALLBACK_PHONE ? 'alternate' :
              safePhone === TEST_PHONE ? 'test' : 'primary'
        );
        setStep('verify');
        startCooldown();
        toastMessage(`OTP sent to +${PHONE_PREFIX} ${getMaskedPhone(safePhone)}`, 'success', 'otpSend');
      } else {
        setError(message || 'Failed to send OTP');
        toastMessage(message || 'Failed to send OTP', 'error', 'otpSend');
      }
    } catch (err) {
      const msg = err?.response?.data?.message || err?.message || 'Failed to send OTP';
      setError(msg);
      toastMessage(msg, 'error', 'otpSend');
    } finally {
      setLoading(false);
    }
  };

  const handleResendOtp = async () => {
    if (cooldown > 0 || loading) return;
    setOtp('');
    setError('');
    await handleSendOtp(targetPhone);
  };

  const handleVerifyOtp = async () => {
    if (!otp.trim()) {
      setError('Please enter the OTP');
      return;
    }
    if (!/^\d{4,6}$/.test(otp.trim())) {
      setError('Enter a valid OTP');
      return;
    }

    setError('');
    setLoading(true);
    try {
      const { success, message } = await verifyDownloadOtpApi(
        targetPhone,
        PHONE_PREFIX,
        otp.trim(),
        adminEmail,
        otpPurpose || actionPurpose,
        activityModule
      );
      if (success) {
        const digits =
          normalizePhone10(targetPhone) ||
          String(targetPhone).replace(/\D/g, '').slice(-10);
        onOtpVerified?.({
          phoneLast4: digits.slice(-4),
          purpose: otpPurpose || actionPurpose,
          activityModule: activityModule || '',
        });
        const raw = onSuccess?.();
        await (raw && typeof raw.then === 'function' ? raw : Promise.resolve(raw));
        toastMessage('OTP verified successfully.', 'success', 'otpVerify');
        clearInterval(timerRef.current);
        setStep('send');
        setOtp('');
        setError('');
        setCooldown(0);
        setLoading(false);
        onClose();
      } else {
        setError(message || 'Invalid OTP');
        toastMessage(message || 'Invalid OTP', 'error', 'otpVerify');
      }
    } catch (err) {
      const msg = err?.response?.data?.message || err?.message || 'Verification failed';
      setError(msg);
      toastMessage(msg, 'error', 'otpVerify');
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordBypass = async () => {
    if (!isSuperAdmin) {
      setError('Only super admin can use password verification');
      return;
    }
    if (!adminPassword.trim()) {
      setError('Please enter the bypass password');
      return;
    }

    setError('');
    setLoading(true);
    try {
      const { success, message } = await verifyDownloadPasswordBypassApi(
        adminPassword,
        otpPurpose || actionPurpose,
        activityModule
      );
      if (success) {
        onOtpVerified?.({
          phoneLast4: 'password',
          purpose: otpPurpose || actionPurpose,
          activityModule: activityModule || '',
          bypassMethod: 'admin_password_recheck',
        });
        const raw = onSuccess?.();
        await (raw && typeof raw.then === 'function' ? raw : Promise.resolve(raw));
        toastMessage('Password verified successfully.', 'success', 'otpVerify');
        clearInterval(timerRef.current);
        setStep('send');
        setOtp('');
        setAdminPassword('');
        setShowPasswordBypass(false);
        setError('');
        setCooldown(0);
        setLoading(false);
        onClose();
      } else {
        setError(message || 'Invalid password');
        toastMessage(message || 'Invalid password', 'error', 'otpVerify');
      }
    } catch (err) {
      const msg = err?.response?.data?.message || err?.message || 'Password verification failed';
      setError(msg);
      toastMessage(msg, 'error', 'otpVerify');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (loading) return;
    clearInterval(timerRef.current);
    setStep('send');
    setOtp('');
    setAdminPassword('');
    setShowPasswordBypass(false);
    setError('');
    setCooldown(0);
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="xs" fullWidth className="custom_modal">
      <DialogTitle sx={{ m: 0, p: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
        <PhoneAndroidIcon fontSize="small" sx={{ color: '#6366f1' }} />
        OTP Verification
        <IconButton
          aria-label="close"
          onClick={handleClose}
          disabled={loading}
          sx={{ position: 'absolute', right: 8, top: 8, color: '#000' }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent>
        {step === 'send' ? (
          <>
            <p className="description" style={{ marginBottom: 12 }}>
              To continue <strong>{actionPurpose}</strong>
              {activityModule ? (
                <>
                  {' '}
                  (<strong>{activityModule}</strong>)
                </>
              ) : null}
              , choose the number for OTP via SMS:
            </p>
            {hasFallback ? (
              <FormControl component="fieldset" variant="standard" sx={{ width: '100%', mb: 1 }}>
                <FormLabel component="legend" sx={{ fontSize: 13, color: '#475569', mb: 0.5 }}>
                  Send OTP to
                </FormLabel>
                <RadioGroup
                  value={smsTarget}
                  onChange={(e) => setSmsTarget(e.target.value)}
                >
                  <FormControlLabel
                    value="primary"
                    control={<Radio size="small" sx={{ py: 0.5 }} />}
                    label={`Mahi +${PHONE_PREFIX} ${getMaskedPhone(PRIMARY_PHONE)}`}
                  />
                  <FormControlLabel
                    value="alternate"
                    control={<Radio size="small" sx={{ py: 0.5 }} />}
                    label={`Gautam +${PHONE_PREFIX} ${getMaskedPhone(FALLBACK_PHONE)}`}
                  />
                  {showAccountantPhone ? (
                    <FormControlLabel
                      value="consultantPayout"
                      control={<Radio size="small" sx={{ py: 0.5 }} />}
                      label={`Accountant +${PHONE_PREFIX} ${getMaskedPhone(CONSULTANT_PAYOUT_PHONE)}`}
                    />
                  ) : null}
                  <FormControlLabel
                    value="test"
                    control={<Radio size="small" sx={{ py: 0.5 }} />}
                    label={`Test +${PHONE_PREFIX} ${getMaskedPhone(TEST_PHONE)}`}
                  />
                </RadioGroup>
              </FormControl>
            ) : (
              <p style={{ fontWeight: 600, color: '#334155', marginBottom: 12, fontSize: 15 }}>
                +{PHONE_PREFIX} {getMaskedPhone(PRIMARY_PHONE)}
              </p>
            )}
            {error && <p style={{ color: '#ef4444', fontSize: 13, marginBottom: 8 }}>{error}</p>}
            {isSuperAdmin ? (
              <div style={{ marginTop: 10 }}>
                {!showPasswordBypass ? (
                  <button
                    type="button"
                    onClick={() => {
                      setShowPasswordBypass(true);
                      setError('');
                    }}
                    disabled={loading}
                    style={{
                      background: 'none',
                      border: 'none',
                      color: '#6366f1',
                      cursor: loading ? 'not-allowed' : 'pointer',
                      fontSize: 13,
                      fontWeight: 600,
                      padding: 0,
                      textDecoration: 'underline',
                    }}
                  >
                    Verify with bypass password
                  </button>
                ) : (
                  <>
                    <TextField
                      autoFocus
                      fullWidth
                      type="password"
                      label="Bypass password"
                      value={adminPassword}
                      onChange={(e) => {
                        setAdminPassword(e.target.value);
                        setError('');
                      }}
                      onKeyDown={(e) => e.key === 'Enter' && handlePasswordBypass()}
                      placeholder="Enter bypass password"
                      variant="outlined"
                      size="small"
                      sx={{ mt: 1 }}
                    />
                    <p style={{ color: '#64748b', fontSize: 12, marginTop: 6, marginBottom: 0 }}>
                      This will be logged as a password-approved OTP bypass.
                    </p>
                  </>
                )}
              </div>
            ) : null}
          </>
        ) : (
          <>
            <p className="description" style={{ marginBottom: 4 }}>
              Enter the OTP sent to <strong>+{PHONE_PREFIX} {getMaskedPhone(targetPhone)}</strong>
            </p>
            <TextField
              autoFocus
              fullWidth
              type="text"
              inputMode="numeric"
              label="Enter OTP"
              value={otp}
              onChange={(e) => {
                const val = e.target.value.replace(/\D/g, '').slice(0, 6);
                setOtp(val);
                setError('');
              }}
              onKeyDown={(e) => e.key === 'Enter' && handleVerifyOtp()}
              error={!!error}
              helperText={error}
              placeholder="Enter OTP"
              variant="outlined"
              size="small"
              sx={{ mt: 1, mb: 1 }}
              inputProps={{ maxLength: 6, style: { fontWeight: 700, letterSpacing: '0.25em' } }}
            />
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 4 }}>
              <span style={{ fontSize: 12, color: '#64748b' }}>Didn&apos;t receive it?</span>
              <button
                type="button"
                onClick={handleResendOtp}
                disabled={cooldown > 0 || loading}
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: cooldown > 0 || loading ? 'not-allowed' : 'pointer',
                  color: cooldown > 0 || loading ? '#94a3b8' : '#6366f1',
                  fontSize: 12,
                  fontWeight: 600,
                  padding: 0,
                  textDecoration: 'underline',
                }}
              >
                {cooldown > 0 ? `Resend in ${cooldown}s` : 'Resend OTP'}
              </button>
            </div>
          </>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 2, pb: 2, gap: 1 }}>
        <Button onClick={handleClose} disabled={loading}>Cancel</Button>
        {step === 'send' ? (
          <>
            {showPasswordBypass ? (
              <Button onClick={handlePasswordBypass} disabled={loading}>
                {loading ? (
                  <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <CircularProgress size={14} color="inherit" /> Verifying...
                  </span>
                ) : (
                  'Verify Password'
                )}
              </Button>
            ) : (
              <Button
                onClick={() =>
                  handleSendOtp(hasFallback ? phoneForSmsTarget(smsTarget) : PRIMARY_PHONE)
                }
                disabled={loading}
              >
                {loading ? (
                  <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <CircularProgress size={14} color="inherit" /> Sending...
                  </span>
                ) : (
                  'Send OTP'
                )}
              </Button>
            )}
          </>
        ) : (
          <Button onClick={handleVerifyOtp} disabled={loading}>
            {loading ? (
              <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <CircularProgress size={14} color="inherit" /> Verifying...
              </span>
            ) : (
              'Verify OTP'
            )}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default DownloadOtpModal;
